import { useState } from "react";
import { useCart } from "../context/CartContext";
import { urlFor } from "../sanity/client";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID = "service_fboeg38";
const EMAILJS_PUBLIC_KEY = "zoZZ3t0fSrCVNMZvK";
const TEMPLATE_ADMIN = "template_kzygcs9";
const TEMPLATE_CUSTOMER = "template_knzx8yd";

function CartDrawer() {
  const {
    cartItems, isCartOpen, setIsCartOpen,
    removeFromCart, updateQuantity, clearCart, totalItems,
  } = useCart();

  const [enquirySent, setEnquirySent] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", address: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleEnquiry = async () => {
    if (!form.name || !form.email || !form.phone) return;
    setSubmitting(true);

    const itemsList = cartItems.map(
      (item) => `${item.name} (Qty: ${item.quantity})${item.price ? ` — ₹${(item.price * item.quantity).toLocaleString("en-IN")}` : " — Price on request"}`
    ).join(", ");

    const templateParams = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      message: `Delivery City: ${form.city || "Not specified"}\nAddress: ${form.address || "Not specified"}\n${form.message || ""}`,
      items: itemsList,
      interest: "Cart Enquiry",
      first_name: form.name.split(" ")[0],
    };

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, TEMPLATE_ADMIN, templateParams, EMAILJS_PUBLIC_KEY);
      try {
        await emailjs.send(EMAILJS_SERVICE_ID, TEMPLATE_CUSTOMER, templateParams, EMAILJS_PUBLIC_KEY);
      } catch (err) {
        console.warn("Customer email failed:", err);
      }
      setSubmitting(false);
      setEnquirySent(true);
      clearCart();
    } catch (err) {
      console.error("EmailJS error:", err);
      setSubmitting(false);
    }
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* BACKDROP */}
      <div
        onClick={() => setIsCartOpen(false)}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 900,
          animation: "fadeBackdrop 0.2s ease",
        }}
      />

      {/* DRAWER */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "420px", maxWidth: "100vw",
        background: "white",
        zIndex: 901,
        display: "flex", flexDirection: "column",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.15)",
        animation: "slideInRight 0.28s ease",
      }}>

        {/* HEADER */}
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex", alignItems: "center",
          justifyContent: "space-between",
          background: "white",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              width: "36px", height: "36px", borderRadius: "10px",
              background: "#eaf4ef",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "18px",
            }}>🛒</div>
            <div>
              <h3 style={{
                fontFamily: "'Sora', sans-serif",
                fontSize: "16px", fontWeight: "800",
                color: "#1a1a2e", margin: 0,
              }}>
                My Cart
              </h3>
              <p style={{
                fontSize: "12px", color: "#9ca3af",
                fontFamily: "'DM Sans', sans-serif", margin: 0,
              }}>
                {totalItems} item{totalItems !== 1 ? "s" : ""} added
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsCartOpen(false)}
            style={{
              background: "#f3f4f6", border: "none",
              borderRadius: "50%", width: "36px", height: "36px",
              cursor: "pointer", fontSize: "18px",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#e5e7eb"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#f3f4f6"}
          >
            ✕
          </button>
        </div>

        {/* CONTENT */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>

          {/* ENQUIRY SENT SUCCESS */}
          {enquirySent && (
            <div style={{
              textAlign: "center", padding: "48px 24px",
              animation: "fadeUp 0.3s ease",
            }}>
              <div style={{ fontSize: "52px", marginBottom: "16px" }}>✅</div>
              <h3 style={{
                fontFamily: "'Sora', sans-serif",
                fontSize: "20px", fontWeight: "900",
                color: "#1a1a2e", marginBottom: "10px",
              }}>
                Enquiry Sent!
              </h3>
              <p style={{
                fontSize: "14px", color: "#6b7280",
                fontFamily: "'DM Sans', sans-serif",
                lineHeight: "1.6", marginBottom: "24px",
              }}>
                Our team will contact you within 24 hours with pricing and availability.
              </p>
              <button
                onClick={() => { setEnquirySent(false); setShowForm(false); setIsCartOpen(false); }}
                style={{
                  background: "#2d6a4f", color: "white",
                  border: "none", borderRadius: "10px",
                  padding: "12px 28px", fontSize: "14px",
                  fontWeight: "700", cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                Continue Shopping
              </button>
            </div>
          )}

          {/* EMPTY CART */}
          {!enquirySent && cartItems.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 24px" }}>
              <div style={{ fontSize: "52px", marginBottom: "16px", opacity: 0.3 }}>🛒</div>
              <h3 style={{
                fontFamily: "'Sora', sans-serif",
                fontSize: "18px", fontWeight: "800",
                color: "#1a1a2e", marginBottom: "8px",
              }}>
                Your cart is empty
              </h3>
              <p style={{
                fontSize: "13.5px", color: "#9ca3af",
                fontFamily: "'DM Sans', sans-serif",
                marginBottom: "24px",
              }}>
                Add products to send a bulk enquiry
              </p>
              <button
                onClick={() => setIsCartOpen(false)}
                style={{
                  background: "#eaf4ef", color: "#2d6a4f",
                  border: "1.5px solid #c2ddd0",
                  borderRadius: "10px", padding: "10px 24px",
                  fontSize: "13px", fontWeight: "700",
                  cursor: "pointer", fontFamily: "'Sora', sans-serif",
                }}
              >
                Browse Products
              </button>
            </div>
          )}

          {/* CART ITEMS */}
          {!enquirySent && cartItems.length > 0 && !showForm && (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {cartItems.map((item) => (
                <div key={item._id} style={{
                  background: "#fafafa",
                  borderRadius: "12px",
                  border: "1.5px solid #edeef0",
                  padding: "14px 16px",
                  display: "flex", gap: "14px",
                  alignItems: "flex-start",
                }}>
                  <div style={{
                    width: "64px", height: "64px",
                    borderRadius: "10px", overflow: "hidden",
                    background: "#eaf4ef", flexShrink: 0,
                    display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: "24px",
                    border: "1px solid #e5e7eb",
                  }}>
                    {item.image ? (
                      <img
                        src={urlFor(item.image).width(64).height(64).fit("crop").url()}
                        alt={item.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : "📦"}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{
                      fontFamily: "'Sora', sans-serif",
                      fontSize: "13.5px", fontWeight: "700",
                      color: "#1a1a2e", margin: "0 0 4px",
                      whiteSpace: "nowrap", overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}>
                      {item.name}
                    </h4>
                    {item.price && (
                      <p style={{
                        fontSize: "13px", color: "#2d6a4f",
                        fontFamily: "'Sora', sans-serif",
                        fontWeight: "800", margin: "0 0 10px",
                      }}>
                        ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                      </p>
                    )}
                    {!item.price && (
                      <p style={{
                        fontSize: "12px", color: "#9ca3af",
                        fontFamily: "'DM Sans', sans-serif",
                        margin: "0 0 10px",
                      }}>
                        Price on request
                      </p>
                    )}

                    <div style={{
                      display: "flex", alignItems: "center",
                      justifyContent: "space-between",
                    }}>
                      <div style={{
                        display: "flex", alignItems: "center",
                        border: "1.5px solid #e5e7eb",
                        borderRadius: "8px", overflow: "hidden",
                        background: "white",
                      }}>
                        <button
                          onClick={() => updateQuantity(item._id, item.quantity - 1)}
                          style={{
                            width: "30px", height: "30px",
                            border: "none", background: "transparent",
                            cursor: "pointer", fontSize: "16px",
                            color: "#374151", transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                        >−</button>
                        <span style={{
                          width: "32px", textAlign: "center",
                          fontSize: "13px", fontWeight: "700",
                          color: "#1a1a2e",
                          fontFamily: "'Sora', sans-serif",
                        }}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item._id, item.quantity + 1)}
                          style={{
                            width: "30px", height: "30px",
                            border: "none", background: "transparent",
                            cursor: "pointer", fontSize: "16px",
                            color: "#374151", transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                        >+</button>
                      </div>

                      <button
                        onClick={() => removeFromCart(item._id)}
                        style={{
                          background: "#fef2f2", border: "none",
                          borderRadius: "8px", padding: "6px 10px",
                          cursor: "pointer", fontSize: "12px",
                          color: "#dc2626", fontWeight: "600",
                          fontFamily: "'DM Sans', sans-serif",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#fee2e2"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "#fef2f2"}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={clearCart}
                style={{
                  background: "none", border: "none",
                  color: "#9ca3af", fontSize: "12px",
                  cursor: "pointer", textAlign: "center",
                  fontFamily: "'DM Sans', sans-serif",
                  padding: "4px",
                  textDecoration: "underline",
                }}
              >
                Clear all items
              </button>
            </div>
          )}

          {/* ENQUIRY FORM */}
          {!enquirySent && showForm && (
            <div style={{ animation: "fadeUp 0.25s ease" }}>
              <button
                onClick={() => setShowForm(false)}
                style={{
                  background: "none", border: "none",
                  color: "#6b7280", fontSize: "13px",
                  cursor: "pointer", marginBottom: "16px",
                  fontFamily: "'DM Sans', sans-serif",
                  display: "flex", alignItems: "center", gap: "4px",
                }}
              >
                ← Back to cart
              </button>

              <h4 style={{
                fontFamily: "'Sora', sans-serif",
                fontSize: "15px", fontWeight: "800",
                color: "#1a1a2e", marginBottom: "6px",
              }}>
                Send Bulk Enquiry
              </h4>
              <p style={{
                fontSize: "12.5px", color: "#6b7280",
                fontFamily: "'DM Sans', sans-serif",
                marginBottom: "20px", lineHeight: "1.5",
              }}>
                {cartItems.length} product{cartItems.length > 1 ? "s" : ""} • Our team will respond within 24hrs
              </p>

              <div style={{
                background: "#f8fafc", borderRadius: "10px",
                border: "1px solid #edeef0", padding: "12px 16px",
                marginBottom: "20px",
              }}>
                {cartItems.map((item, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between",
                    fontSize: "12.5px", color: "#374151",
                    fontFamily: "'DM Sans', sans-serif",
                    padding: "4px 0",
                    borderBottom: i < cartItems.length - 1 ? "1px solid #f0f0f0" : "none",
                  }}>
                    <span>{item.name}</span>
                    <span style={{ fontWeight: "600" }}>Qty: {item.quantity}</span>
                  </div>
                ))}
              </div>

              {[
                { key: "name", label: "Full Name *", placeholder: "Your name", type: "text" },
                { key: "email", label: "Email Address *", placeholder: "your@email.com", type: "email" },
                { key: "phone", label: "Phone Number *", placeholder: "+91 98765 43210", type: "tel" },
                { key: "city", label: "Delivery City", placeholder: "e.g. Delhi, Mumbai, Bangalore", type: "text" },
{ key: "address", label: "Full Address", placeholder: "House/Flat No, Street, Area, Pincode", type: "text" },
              ].map((field) => (
                <div key={field.key} style={{ marginBottom: "14px" }}>
                  <label style={{
                    display: "block", fontSize: "11px",
                    fontWeight: "700", color: "#374151",
                    fontFamily: "'Sora', sans-serif",
                    marginBottom: "6px", letterSpacing: "0.3px",
                  }}>
                    {field.label}
                  </label>
                  <input
                    type={field.type}
                    placeholder={field.placeholder}
                    value={form[field.key]}
                    onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                    style={{
                      width: "100%", padding: "10px 14px",
                      border: "1.5px solid #e5e7eb",
                      borderRadius: "9px", fontSize: "13.5px",
                      fontFamily: "'DM Sans', sans-serif",
                      outline: "none", transition: "border-color 0.2s",
                      background: "#fafafa",
                    }}
                    onFocus={(e) => e.target.style.borderColor = "#2d6a4f"}
                    onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
                  />
                </div>
              ))}

              <div style={{ marginBottom: "20px" }}>
                <label style={{
                  display: "block", fontSize: "11px",
                  fontWeight: "700", color: "#374151",
                  fontFamily: "'Sora', sans-serif",
                  marginBottom: "6px", letterSpacing: "0.3px",
                }}>
                  ADDITIONAL MESSAGE
                </label>
                <textarea
                  placeholder="Any specific requirements, delivery location, etc."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  style={{
                    width: "100%", padding: "10px 14px",
                    border: "1.5px solid #e5e7eb",
                    borderRadius: "9px", fontSize: "13.5px",
                    fontFamily: "'DM Sans', sans-serif",
                    outline: "none", resize: "vertical",
                    transition: "border-color 0.2s",
                    background: "#fafafa",
                  }}
                  onFocus={(e) => e.target.style.borderColor = "#2d6a4f"}
                  onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
                />
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        {!enquirySent && cartItems.length > 0 && (
          <div style={{
            padding: "16px 24px",
            borderTop: "1px solid #f0f0f0",
            background: "white",
          }}>
            {!showForm ? (
              <button
                onClick={() => setShowForm(true)}
                style={{
                  width: "100%", padding: "14px",
                  background: "#2d6a4f", color: "white",
                  border: "none", borderRadius: "12px",
                  fontSize: "15px", fontWeight: "800",
                  cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                  boxShadow: "0 6px 20px rgba(45,106,79,0.3)",
                  transition: "all 0.18s ease",
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
              >
                Send Enquiry for {totalItems} Item{totalItems > 1 ? "s" : ""} →
              </button>
            ) : (
              <button
                onClick={handleEnquiry}
                disabled={submitting || !form.name || !form.email || !form.phone}
                style={{
                  width: "100%", padding: "14px",
                  background: submitting || !form.name || !form.email || !form.phone
                    ? "#9ca3af" : "#2d6a4f",
                  color: "white", border: "none",
                  borderRadius: "12px", fontSize: "15px",
                  fontWeight: "800", cursor: submitting ? "not-allowed" : "pointer",
                  fontFamily: "'Sora', sans-serif",
                  transition: "all 0.18s ease",
                }}
              >
                {submitting ? "Sending..." : "Submit Enquiry"}
              </button>
            )}

            <p style={{
              textAlign: "center", fontSize: "11.5px",
              color: "#9ca3af", fontFamily: "'DM Sans', sans-serif",
              margin: "10px 0 0",
            }}>
              Our team responds within 24 hours
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0); opacity: 1; }
        }
        @keyframes fadeBackdrop {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}

export default CartDrawer;